/**
 * The View As banner (docs/01 §6, docs/12 F-D).
 *
 * Matt on v1's version: "this was a big feature in v1 but never really worked
 * correctly." The single most likely way to reproduce that is a borrowed session
 * that looks like a real one — an admin forgets, reads a screen as somebody
 * else, and reports what they saw as the truth about their own access.
 *
 * So this sits above everything, on every route, and carries the exit. It is
 * `role="alert"` rather than a quiet chip because "you are not yourself right
 * now" is the highest-priority fact on the page, and it names BOTH people:
 * whose eyes, and whose account you will return to.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import type { ReactNode } from 'react';

import { api, type Me } from '../../app/api.js';
import { Button } from '../../components/ui/index.js';

export function ViewAsBanner({ me }: { readonly me: Me }): ReactNode {
  const client = useQueryClient();
  const stop = useMutation({
    mutationFn: () => api('/api/v1/impersonation/stop', { method: 'POST' }),
    // Everything cached was fetched as the other person, so none of it is true
    // any more. Clear the lot rather than invalidating screen by screen.
    onSettled: async () => {
      await client.invalidateQueries();
    },
  });

  if (me.impersonation === null) return null;
  const { viewingName, adminDisplayName, mode } = me.impersonation;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-gold-tint px-3 py-2 text-center text-meta font-semibold text-gold-text"
    >
      <span className="flex items-center gap-1.5">
        <Eye size={15} aria-hidden />
        {mode === 'view'
          ? `You are viewing PropOS as ${viewingName} — read only.`
          : `You are acting as ${viewingName}. Changes are recorded as yours.`}
      </span>
      <Button
        variant="secondary"
        busy={stop.isPending}
        onClick={() => {
          stop.mutate();
        }}
      >
        {`Back to ${adminDisplayName}`}
      </Button>
    </div>
  );
}
